
import { Injectable, Inject, NgZone, PLATFORM_ID, Optional } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { catchError, filter, first, map, switchMap, tap, finalize } from 'rxjs/operators';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import { BehaviorSubject, Observable, of, Subject, from, EMPTY, throwError } from 'rxjs';
import { LoggingService } from 'src/app/shared/services/Logging.service';
import { RemoteApiService } from 'src/app/shared/services/remote-api.service';
import { environment } from 'src/app/environments/environments';
import { EncryptionService } from './encryption.service';
import { isPlatformBrowser } from '@angular/common';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { clearShopState } from 'src/app/shared/ngrxState/shop.actions';

/** Hard cap for a browser login. Refreshing the page does not extend it. */
const SESSION_MAX_MS = 6 * 60 * 60 * 1000;
const SESSION_STARTED_KEY = 'conversellSessionStartedAt';
const SESSION_CHECK_MS = 30_000;

let sessionExpiryWatchStarted = false;
let sessionExpiryLogoutInProgress = false;
let freshSignInPending = false;
/** True while signup is creating the Firestore profile. getUser must not fetch it yet. */
let profileCreatePending = false;


export interface User {
  pageID?: string[];
  id: string;
  userParentId?: string;
  totalBilling?: string;
  email: string;
  displayName?: string;
  cell?: string;
  role?: string;
  photoURL?: string;
  resumeURL?: string;
  nationalId?: string;
  gender?: string;
  skills?: string[];
  operations?: any[];
  shops?: {};
  themes?: string[];
  customer_id_stripe?: any;
  stripeCustomerId?: string;
  platform?: any;
  emailVerified?: any;
  userLogin?: any;
  status?: string;
  marketing_communications?: boolean;
}

@Injectable()
export class AuthService {
  private readonly STORAGE_KEY = environment.localStorage.user;
  private readonly ADMIN_ID_KEY = 'adminID';
  /** Firebase uid of the account a SuperAdmin connected to. */
  private readonly CONNECTED_UID_KEY = 'conversellConnectedUid';

  // Start with cached user if present; will be reconciled with Firebase auth on getUser().
  private currentUserSubject = new BehaviorSubject<User | null>(this.getStoredUser());
  public currentUser$ = this.currentUserSubject.asObservable();

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.isLoggedIn());
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private roleSubject = new BehaviorSubject<string>('');
  public role$ = this.roleSubject.asObservable();

  redirectRouteSnapshot: ActivatedRouteSnapshot | undefined;
  private logoutSubject = new Subject<void>();
  logout$ = this.logoutSubject.asObservable();
  /** True while swapping Firebase users (Connect to / Back to SuperAdmin). */
  private switchingUser = false;
  private connectAsSessionChanged = new Subject<boolean>();
  /** Emits true when Connect-to starts and false when the SuperAdmin id is cleared. */
  readonly connectAsSessionChanged$ = this.connectAsSessionChanged.asObservable();

  constructor(
    private loggingService: LoggingService,
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private encryptionService: EncryptionService,
    private router: Router,
    private remoteApiService: RemoteApiService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private notificationService: NotificationService,
    private ngZone: NgZone,
    private dialog: MatDialog,
    private store: Store
  ) {
    if (this.isBrowser && this.afAuth) {
      this.afAuth.setPersistence('local');
      this.startSessionExpiryWatch();
    }
  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
   * Login lasts at most 6 hours from the first authenticated moment in this
   * browser. Page reloads and Connect-to keep the same start time.
   */
  isSessionExpired(now = Date.now()): boolean {
    const startedAt = this.readSessionStartedAt();
    if (startedAt == null) {
      return false;
    }
    return now - startedAt >= SESSION_MAX_MS;
  }

  /**
   * A restored Firebase user with no start key must sign in again.
   * A sign-in that is still in flight, or a Connect-to swap, is not expired.
   */
  sessionNeedsReauth(hasFirebaseUser: boolean, now = Date.now()): boolean {
    if (!hasFirebaseUser || freshSignInPending || this.switchingUser) {
      return false;
    }
    if (this.readSessionStartedAt() == null) {
      return true;
    }
    return this.isSessionExpired(now);
  }

  forceSessionExpiryLogout(): Promise<void> {
    if (!this.isBrowser || sessionExpiryLogoutInProgress) {
      return Promise.resolve();
    }
    return this.afAuth.currentUser.then((user) => {
      if (!this.sessionNeedsReauth(!!user)) {
        return;
      }
      return this.runSessionExpiryLogout();
    });
  }

  private runSessionExpiryLogout(): Promise<void> {
    if (sessionExpiryLogoutInProgress) {
      return Promise.resolve();
    }
    sessionExpiryLogoutInProgress = true;
    const returnUrl = this.router.url || '';
    this.notificationService.muteErrorToasts();
    this.ngZone.run(() => {
      if (!returnUrl.startsWith('/auth')) {
        void this.router.navigate(['/auth/login']);
      }
    });
    return this.afAuth.signOut().then(
      () => {
        this.clearSessionStartedAt();
        this.clearConnectAsAdminId();
        this.clearPersistedShopState();
        this.clearUserState();
        try {
          this.dialog.closeAll();
        } catch {
          // dialog overlay may already be gone
        }
        this.store.dispatch(clearShopState());
        this.ngZone.run(() => {
          this.notificationService.unmuteErrorToasts();
          this.notificationService.showWarning(
            'Your session expired after 6 hours. Please sign in again.',
          );
        });
      },
      (error) => {
        this.loggingService.log('session expiry logout failed', error);
        this.notificationService.unmuteErrorToasts();
      },
    ).finally(() => {
      sessionExpiryLogoutInProgress = false;
    });
  }

  private startSessionExpiryWatch(): void {
    if (sessionExpiryWatchStarted) {
      return;
    }
    sessionExpiryWatchStarted = true;

    this.afAuth.authState.subscribe((user) => {
      this.onAuthSession(user);
    });

    this.ngZone.runOutsideAngular(() => {
      setInterval(() => this.enforceSessionExpiry(), SESSION_CHECK_MS);
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.enforceSessionExpiry();
      }
    });

    window.addEventListener('storage', (event) => {
      if (event.key === SESSION_STARTED_KEY && event.newValue == null && event.oldValue != null) {
        void this.forceSessionExpiryLogout();
      }
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.enforceSessionExpiry());
  }

  private onAuthSession(user: firebase.User | null): void {
    if (!this.isBrowser || sessionExpiryLogoutInProgress) {
      return;
    }
    if (!user) {
      if (!this.switchingUser) {
        this.clearUserState();
      }
      return;
    }
    if (this.switchingUser) {
      return;
    }
    if (this.readSessionStartedAt() == null) {
      if (freshSignInPending) {
        freshSignInPending = false;
        this.writeSessionStartedAt(Date.now());
        return;
      }
      void this.forceSessionExpiryLogout();
      return;
    }
    if (this.isSessionExpired()) {
      void this.forceSessionExpiryLogout();
    }
  }

  private enforceSessionExpiry(): void {
    if (!this.isBrowser || sessionExpiryLogoutInProgress || this.switchingUser) {
      return;
    }
    if (!this.isSessionExpired()) {
      return;
    }
    void this.afAuth.currentUser.then((user) => {
      if (!user && !this.isLoggedIn()) {
        this.clearSessionStartedAt();
        return;
      }
      if (user && this.sessionNeedsReauth(true)) {
        void this.forceSessionExpiryLogout();
      }
    });
  }

  private readSessionStartedAt(): number | null {
    if (!this.isBrowser) {
      return null;
    }
    try {
      const raw = window.localStorage.getItem(SESSION_STARTED_KEY);
      if (!raw) {
        return null;
      }
      const startedAt = Number(raw);
      return Number.isFinite(startedAt) && startedAt > 0 ? startedAt : null;
    } catch {
      return null;
    }
  }

  private writeSessionStartedAt(startedAt: number): void {
    if (!this.isBrowser) {
      return;
    }
    try {
      window.localStorage.setItem(SESSION_STARTED_KEY, String(startedAt));
    } catch {
      // ignore quota / locked storage
    }
  }

  private clearSessionStartedAt(): void {
    if (!this.isBrowser) {
      return;
    }
    try {
      window.localStorage.removeItem(SESSION_STARTED_KEY);
    } catch {
      // ignore
    }
  }

  // doVerifyEmail() {
  //   //return this.afAuth.sendEmailVerification();//this.afAuth.currentUser.sendEmailVerification();
  //   return this.afAuth.currentUser.then(u => u.sendEmailVerification());
  // }


  triggerLogout(): Promise<void> {
    return new Promise((resolve) => {
      this.notificationService.muteErrorToasts();
      this.logoutSubject.next();
      this.loggingService.log('Sidebar cleaned up');
      resolve();
    });
  }

  currentUser() {
    return this.afAuth.currentUser;
  }

  getUser(): Observable<any> | null {
    return this.afAuth.authState.pipe(
      switchMap((fbUser) => {
        if (!fbUser) {
          if (this.switchingUser) {
            return EMPTY;
          }
          this.clearUserState();
          return of(null);
        }
      const cached = this.getCurrentUser();
      if (cached && cached.id === fbUser.uid) {
        this.updateUserObservables(cached);
        return of(cached);
      }
      if (profileCreatePending) {
        return EMPTY;
      }
      return this.getUserByUid(fbUser.uid).pipe(
          map(res => res?.user || null)
        );
      })
    );
  }

  async doEmailLogin(email: string, password: string) {
    freshSignInPending = true;
    return this.afAuth.signInWithEmailAndPassword(email, password);
  }

  AuthLogin(provider: any) {
    // Explicitly add scopes for Facebook provider to ensure permissions are requested
    if (provider.providerId === 'facebook.com') {
      provider.addScope('email');
      provider.setCustomParameters({
        display: 'popup',
        scope: 'email'
      });
    } else {
      provider.setCustomParameters({ display: 'popup' });
    }
    freshSignInPending = true;
    return this.afAuth.signInWithPopup(provider);
  }

  /**
   * Send email verification when new user signs up
   */
  SendVerificationMail(email: string, shopName?: string, cms?: string) {
    let continueUrl = window.location.origin + '/auth/login';
    if (shopName && cms) {
      continueUrl = window.location.origin + '/auth/signup-details?shopName=' + shopName + '&cms=' + cms;
    }
    return this.afAuth.currentUser
      .then((u: any) =>
        u.sendEmailVerification({ url: continueUrl, handleCodeInApp: true })
      )
      .then(() => {
        window.localStorage.setItem('emailForSignIn', email);
        this.router.navigate(['auth/email-verification']);
      });
  }

  SignUpWithEmail(email: string, password: string, marketing_communications: boolean = false): Observable<any> {
    return new Observable(observer => {
      // Same as login: a new Firebase user with no session key must not be
      // signed out before createUser can send the ID token.
      freshSignInPending = true;
      profileCreatePending = true;
      this.afAuth
        .createUserWithEmailAndPassword(email, password)
        .then(async (result) => {
          if (result.user) {
            await result.user.getIdToken(true);
          }
          this.createUser({ id: result.user?.uid || '', email, marketing_communications }).subscribe({
            next: (response: any) => {
              profileCreatePending = false;
              if (response.success) {
                observer.next({ success: true, message: 'User created successfully' });
              } else {
                observer.next({ success: false, message: response.error });
              }
              observer.complete();
            },
            error: (error: any) => {
              profileCreatePending = false;
              observer.next({ success: false, message: error.message });
              observer.complete();
            }
          });
        })
        .catch((error) => {
          freshSignInPending = false;
          profileCreatePending = false;
          console.log('Something went wrong');
          observer.next({ success: false, message: error.message });
          observer.complete();
        });
    });
  }

  doResetPassword(email: string) {
    const actionCodeSettings: any = this.buildPasswordResetActionSettings();
    if (actionCodeSettings) {
      return this.afAuth.sendPasswordResetEmail(email, actionCodeSettings);
    }
    return this.afAuth.sendPasswordResetEmail(email);
  }

  private buildPasswordResetActionSettings(): any | null {
    if (!this.isBrowser) {
      return null;
    }
    try {
      const origin = window.location.origin;
      if (!origin) {
        return null;
      }
      return {
        url: `${origin}/auth/action`,
        handleCodeInApp: false,
      };
    } catch {
      return null;
    }
  }

  verifyPasswordResetCode(oobCode: string): Promise<string> {
    return this.afAuth.verifyPasswordResetCode(oobCode);
  }

  confirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
    return this.afAuth.confirmPasswordReset(oobCode, newPassword);
  }

  /** Marks the Firebase account verified. Used by the email-verification link. */
  applyActionCode(oobCode: string): Promise<void> {
    return this.afAuth.applyActionCode(oobCode).then(async () => {
      const user = await this.afAuth.currentUser;
      if (user) {
        await user.reload();
      }
    });
  }

  doTokenLoginShopify(token: string): Promise<any> {
    if (!this.isBrowser || !this.afAuth) {
      return Promise.reject('Firebase Auth not available on server side');
    }

    return firebase
      .auth()
      .setPersistence(firebase.auth.Auth.Persistence.LOCAL)
      .then(() => {
        freshSignInPending = true;
        return this.afAuth!.signInWithCustomToken(token);
      })
      .then((result) => {
        if (!this.afs) {
          throw new Error('Firestore not available');
        }
        return this.afs
          .doc<User>(`users/${result.user?.uid}`)
          .valueChanges()
          .pipe(first())
          .toPromise()
          .then((userData) => {
            if (userData) {
              // Store user data in local storage
              if (this.isBrowser) {
                localStorage.setItem('user', JSON.stringify(userData));
              }
              return userData; // Return the user data including the role
            } else {
              throw new Error('User data not found in Firestore');
            }
          });
      })
      .catch((error) => {
        console.log('Something went wrong');
        this.loggingService.log('Error during token login:', error);
        throw error;
      });
  }

  async doSignout() {
    this.clearSessionStartedAt();
    this.clearPersistedShopState();
    this.clearExtraLogoutKeys();
    this.notificationService.muteErrorToasts();
    await this.afAuth.signOut().then(
      () => {
        this.clearConnectAsAdminId();
        this.clearUserState();
        this.redirectRouteSnapshot = undefined;
        this.loggingService.log('logged out');
      }
    ).catch(
      (e) => {
        console.log('Something went wrong');
        this.loggingService.log('logged out catch', e);
      }
    );
  }

  /** Keys that used to disappear only because logout called localStorage.clear(). */
  private clearExtraLogoutKeys(): void {
    if (!this.isBrowser) {
      return;
    }
    window.localStorage.removeItem('user');
    window.localStorage.removeItem('emailForSignIn');
    window.localStorage.removeItem('conv_opened_ids');
    window.localStorage.removeItem('conv_last_opened_at');
    try {
      window.sessionStorage.removeItem('amazon_oauth_state');
    } catch {
      // ignore
    }
  }

  signOut() {
    return this.doSignout().then(() => {
      this.router.navigate(['auth', 'login']);
    });
  }

  /**
   * SuperAdmin uid for a live “Connect to” session. Stored in localStorage and
   * sessionStorage so getUserByUid / role checks cannot drop it.
   */
  setConnectAsAdminId(adminId: string): void {
    if (!this.isBrowser || !adminId || adminId === 'null' || adminId === 'undefined') {
      return;
    }
    window.localStorage.setItem(this.ADMIN_ID_KEY, adminId);
    try {
      window.sessionStorage.setItem(this.ADMIN_ID_KEY, adminId);
    } catch {
      // sessionStorage can throw in locked-down browsers
    }
    this.connectAsSessionChanged.next(true);
  }

  getConnectAsAdminId(): string | null {
    if (!this.isBrowser) {
      return null;
    }
    let stored: string | null = null;
    try {
      stored =
        window.localStorage.getItem(this.ADMIN_ID_KEY) ||
        window.sessionStorage.getItem(this.ADMIN_ID_KEY);
    } catch {
      stored = window.localStorage.getItem(this.ADMIN_ID_KEY);
    }
    if (!stored || stored === 'null' || stored === 'undefined') {
      return null;
    }
    return stored;
  }

  clearConnectAsAdminId(): void {
    if (!this.isBrowser) {
      return;
    }
    const hadSession = this.hasConnectAsSession();
    window.localStorage.removeItem(this.ADMIN_ID_KEY);
    window.localStorage.removeItem(this.CONNECTED_UID_KEY);
    window.localStorage.removeItem('adminTargetWorkspaceId');
    try {
      window.sessionStorage.removeItem(this.ADMIN_ID_KEY);
      window.sessionStorage.removeItem(this.CONNECTED_UID_KEY);
    } catch {
      // ignore
    }
    if (hadSession) {
      this.connectAsSessionChanged.next(false);
    }
  }

  /**
   * SuperAdmin uid from the impersonation custom-token claim, if present.
   */
  async getImpersonatorIdFromToken(forceRefresh = false): Promise<string | null> {
    const user = await this.afAuth.currentUser;
    if (!user) {
      return null;
    }
    try {
      const token = await user.getIdTokenResult(forceRefresh);
      const id = token?.claims?.['impersonatorId'];
      return typeof id === 'string' && id && id !== 'null' ? id : null;
    } catch {
      return null;
    }
  }

  /**
   * SuperAdmin uid to restore after Connect to.
   * Prefer the token claim — localStorage can be overwritten with the
   * impersonated admin's id, which would "return" to the same workspace.
   */
  async resolveSuperAdminReturnId(): Promise<string | null> {
    const currentUid = (await this.afAuth.currentUser)?.uid || null;
    const fromToken = await this.getImpersonatorIdFromToken(true);
    const fromStorage = this.getConnectAsAdminId();
    const candidates = [fromToken, fromStorage].filter(
      (id): id is string => !!id && id !== currentUid,
    );
    return candidates[0] || null;
  }

  beginUserSwitch(): void {
    this.switchingUser = true;
  }

  endUserSwitch(): void {
    this.switchingUser = false;
  }

  isSwitchingUser(): boolean {
    return this.switchingUser;
  }

  /**
   * Restore the SuperAdmin Firebase session after Connect to.
   * Resolves the admin id from the token claim or storage; when it is
   * missing, the backend resolves it from the impersonation session.
   */
  returnToSuperAdminResolved(): Observable<boolean> {
    return from(this.resolveSuperAdminReturnId()).pipe(
      switchMap((id) => {
        if (id) {
          return this.returnToSuperAdmin(id);
        }
        return this.returnToSuperAdminViaBackend();
      }),
    );
  }

  returnToSuperAdminViaBackend(): Observable<boolean> {
    this.beginUserSwitch();
    const url = `${environment.administratorManager}/login-as-user`;
    return (
      this.remoteApiService.postApi(url, { returnToAdmin: true }) as Observable<any>
    ).pipe(
      switchMap((response: any) => {
        const customToken = response?.result?.token;
        if (!customToken) {
          return of(false);
        }
        return from(this.afAuth.signInWithCustomToken(customToken)).pipe(
          switchMap((cred) => {
            const uid = cred.user?.uid;
            if (!cred.user || !uid) {
              return of(false);
            }
            this.clearConnectAsAdminId();
            return from(cred.user.getIdToken(true)).pipe(
              switchMap(() => this.getUserByUid(uid)),
              map(
                (res: any) =>
                  !!(res?.success && res.user?.role === 'SuperAdmin'),
              ),
            );
          }),
        );
      }),
      tap((ok) => {
        if (ok) {
          this.clearConnectAsAdminId();
        }
      }),
      catchError(() => of(false)),
      finalize(() => this.endUserSwitch()),
    );
  }
  returnToSuperAdmin(superAdminId: string): Observable<boolean> {
    this.beginUserSwitch();
    const url = `${environment.administratorManager}/login-as-user`;
    const mint = (body: Record<string, unknown>) =>
      this.remoteApiService.postApi(url, body) as Observable<any>;

    return mint({ targetUserId: superAdminId, returnToAdmin: true }).pipe(
      catchError((err) => {
        if (err?.status === 400) {
          return mint({ targetUserId: superAdminId });
        }
        return throwError(() => err);
      }),
      switchMap((response: any) => {
        const customToken = response?.result?.token;
        if (!customToken) {
          return of(false);
        }
        return from(this.afAuth.signInWithCustomToken(customToken)).pipe(
          switchMap((cred) => {
            const signedIn = cred.user;
            const uid = signedIn?.uid;
            if (!signedIn || !uid || uid !== superAdminId) {
              return of(false);
            }
            this.clearConnectAsAdminId();
            return from(signedIn.getIdToken(true)).pipe(
              switchMap(() => this.getUserByUid(uid)),
              map(
                (res: any) =>
                  !!(res?.success && res.user?.role === 'SuperAdmin'),
              ),
            );
          }),
        );
      }),
      tap((ok) => {
        if (ok) {
          this.clearConnectAsAdminId();
        }
      }),
      finalize(() => this.endUserSwitch()),
    );
  }

  /** Drop the connected admin's shop so SuperAdmin console does not reopen it. */
  clearPersistedShopState(): void {
    if (!this.isBrowser) {
      return;
    }
    window.localStorage.removeItem('shop');
    window.localStorage.removeItem('@@STATE');
    try {
      window.sessionStorage.removeItem('shop');
    } catch {
      // ignore
    }
  }

  /**
   * Clear user state and reset observables.
   * Does not touch the Connect-to SuperAdmin id — that must survive user swaps
   * during impersonation (getUserByUid) so Log out can return to SuperAdmin.
   */
  private clearUserState(): void {
    try {
      if (this.isBrowser) {
        this.encryptionService.removeItem(this.STORAGE_KEY);
      }
      this.currentUserSubject.next(null);
      this.isAuthenticatedSubject.next(false);
      this.roleSubject.next('');
      this.loggingService.log('User state cleared');
    } catch (error) {
      this.loggingService.log('Error clearing user state:', error);
    }
  }

  async checkAccountEmailExistInFirebase(email: string): Promise<boolean> {
    try {
      this.loggingService.log('Checking email existence:', email);
      const signInMethods = await firebase.auth().fetchSignInMethodsForEmail(email);
      const exists = signInMethods && signInMethods.length > 0;
      this.loggingService.log('Email exists:', exists, 'Sign-in methods:', signInMethods);
      return exists;
    } catch (error: any) {
      this.loggingService.log('Error checking email existence:', error);
      return false;
    }
  }

  /**
   * Returns an observable that emits `true` if the current token has `adminLogin` claim.
   * Uses `getIdTokenResult()` off the signed-in user so claims match the latest token after impersonation.
   */
  checkIfAdminAsUser(): Observable<boolean> {
    return this.afAuth.authState.pipe(
      switchMap((user) => {
        if (!user) {
          return of(false);
        }
        return from(user.getIdTokenResult(false)).pipe(
          map((idTokenResult: firebase.auth.IdTokenResult) => {
            const claims = idTokenResult?.claims || {};
            const v = claims['adminLogin'];
            return v === true || v === 'true';
          }),
          catchError(() => of(false)),
        );
      }),
    );
  }

  /**
   * Check if user is admin by UID
   * @param uid - Firebase user UID
   * @returns Observable<boolean> - true if user is admin, manager, or SuperAdmin
   */
  checkIfAdmin(uid: string): Observable<boolean> {
    const url = `${environment.userManagerUrl}getUserByUid/${uid}`;
    return this.remoteApiService.getApi(url).pipe(
      map((response: any) => {
        if (response && response.success && response.data) {
          const role = response.data.role;
          return (
            role === 'SuperAdmin' ||
            role === 'admin' ||
            role === 'manager'
          );
        }
        return false;
      }),
      catchError((error) => {
        this.loggingService.log('Error checking admin status:', error);
        return of(false);
      })
    );
  }

  /**
   * Check if user is activated by UID
   * @param uid - Firebase user UID
   * @returns Observable<boolean> - true if user status is "active"
   */
  checkIfUserActivated(uid: string): Observable<boolean> {
    const url = `${environment.userManagerUrl}getUserByUid/${uid}`;
    return this.remoteApiService.getApi(url).pipe(
      map((response: any) => {
        if (response && response.success && response.data) {
          return response.data.status === "active";
        }
        return false;
      }),
      catchError((error) => {
        this.loggingService.log('Error checking user activation status:', error);
        return of(false);
      })
    );
  }

  /**
   * Create a new user profile via API
   * @param userData - User data object with required and optional fields
   * @returns Observable<any> - Returns user data on success or error information
   * 
   * Required fields: id (string), email (string)
   * Optional fields: displayName, photoURL, emailVerified, userLogin, status, marketing_communications
   */
  createUser(userData: {
    id: string;
    email: string;
    displayName?: string | null;
    photoURL?: string | null;
    emailVerified?: boolean;
    userLogin?: string | object;
    status?: string;
    marketing_communications?: boolean;
    provider?: string;
  }): Observable<any> {
    const url = `${environment.userManagerUrl}createUser`;

    const requestBody = {
      id: userData.id,
      email: userData.email,
      provider: userData.provider || 'email',
      ...(userData.displayName !== undefined && { displayName: userData.displayName }),
      ...(userData.photoURL !== undefined && { photoURL: userData.photoURL }),
      ...(userData.emailVerified !== undefined && { emailVerified: userData.emailVerified }),
      ...(userData.userLogin !== undefined && { userLogin: userData.userLogin }),
      ...(userData.status !== undefined && { status: userData.status }),
      ...(userData.marketing_communications !== undefined && { marketing_communications: userData.marketing_communications }),
    };

    return this.remoteApiService.postApi(url, requestBody).pipe(
      map((response: any) => {
        if (response && response.success && response.data) {
          const createdUser = response.data;
          // Store user and update observables (currentUser$, isAuthenticated$, role$)
          this.storeUser(createdUser);
          this.updateUserObservables(createdUser);
          return {
            success: true,
            data: createdUser,
            error: null
          };
        } else {
          const errorCode = response?.error || 'UNKNOWN_ERROR';
          return {
            success: false,
            data: null,
            error: errorCode
          };
        }
      }),
      catchError((error: any) => {
        this.loggingService.log('Error creating user:', error);

        let errorCode = 'INTERNAL_SERVER_ERROR';
        if (error?.error?.error) {
          errorCode = error.error.error;
        } else if (error?.status === 400) {
          errorCode = 'EMAIL_REQUIRED';
        } else if (error?.status === 409) {
          errorCode = error?.error?.error || 'USER_ALREADY_EXISTS';
        }

        return of({
          success: false,
          data: null,
          error: errorCode
        });
      })
    );
  }

  getUserByUid(uid: string): Observable<any> {
    const url = `${environment.userManagerUrl}getUserByUid/${uid}`;
    return this.remoteApiService.getApi(url).pipe(
      switchMap((response: any) => {
        if (response && response.success && response.data) {
          const user: User = { ...response.data, id: uid || response.data.id };
          if (this.hasConnectAsSession() && user.id && user.id !== this.getConnectAsAdminId()) {
            this.setConnectedAccountId(user.id);
          }
          // Store user and update observables asynchronously to avoid race with previous cached state
          return from((async () => {
            this.clearUserState();
            this.storeUser(user);
            this.updateUserObservables(user);
            return {
              success: true,
              message: 'User fetched successfully',
              user: user
            };
          })());
        } else {
          return of({
            success: false,
            message: response?.error || 'User not found',
            user: null
          });
        }
      }),
      catchError((error: any) => {
        this.loggingService.log('Error fetching user by UID:', error);
        if (error?.status === 404) {
          return of({
            success: false,
            message: 'User not found',
            user: null
          });
        }
        return of({
          success: false,
          message: error?.error?.error || 'Failed to fetch user',
          user: null
        });
      })
    );
  }
  completeRegistration(): Observable<any> {
    const url = `${environment.userManagerUrl}completeRegistration`;
    return this.remoteApiService.postApi(url, {}).pipe(
      map((response: any) => {
        if (response?.success && response.data) {
          this.storeUser(response.data);
          this.updateUserObservables(response.data);
        }
        return response;
      }),
    );
  }

  updateUser(userData: {
    displayName?: string | null;
    cell?: string | null;
    company?: string | null;
    platform?: string | null;
    shop_urls?: string[] | null;
    country?: string | null;
    activity?: string | null;
    role?: string | null;
    pageID?: string[] | null;
    status?: string | null;
    photoURL?: string | null;
    email?: string | null;
    vat?: string | null;
    address1?: string | null;
    address2?: string | null;
    storeurl?: string | null;
    postal?: string | null;
    city?: string | null;
    state?: string | null;
    timezone?: string | null;
  }): Observable<any> {
    const url = `${environment.userManagerUrl}updateUser/${this.getCurrentUser()?.id}`;
    return this.remoteApiService.patchApi(url, userData).pipe(
      map((response: any) => {
        if (response && response.success && response.data) {
          const updatedUser = response.data;
          // Store user and update observables
          this.storeUser(updatedUser);
          this.updateUserObservables(updatedUser);
          return {
            success: true,
            data: updatedUser,
            error: null
          };
        } else {
          const errorCode = response?.error || 'UNKNOWN_ERROR';
          return {
            success: false,
            data: null,
            error: errorCode
          };
        }
      }),
      catchError((error: any) => {
        this.loggingService.log('Error updating user:', error);
        return of({
          success: false,
          data: null,
          error: error?.error?.error || 'Failed to update user'
        });
      })
    );
  }

  /** Merge fields into the cached session user (after profile save). */
  patchCurrentUser(partial: Record<string, any>): void {
    const current = this.getCurrentUser();
    if (!current?.id) {
      return;
    }
    const updated = { ...current, ...partial, id: current.id };
    this.storeUser(updated);
    this.updateUserObservables(updated);
  }

  /** Signup is about to create the profile. Skip getUserByUid until that finishes. */
  beginProfileCreate(): void {
    profileCreatePending = true;
  }

  endProfileCreate(): void {
    profileCreatePending = false;
  }

  isLoggedIn(): boolean {
    const user = this.getStoredUser();
    return !!user;
  }

  private storeUser(user: User): void {
    try {
      this.encryptionService.setItem(this.STORAGE_KEY, user);
      this.loggingService.log('User data encrypted and stored');
    } catch (error) {
      this.loggingService.log('Error storing encrypted user data:', error);
    }
  }

  /**
   * Update all user-related observables
   * This ensures currentUser$, isAuthenticated$, and role$ are synchronized
   */
  private updateUserObservables(user: User | null): void {
    if (user) {
      this.notificationService.unmuteErrorToasts();
      this.currentUserSubject.next(user);
      this.isAuthenticatedSubject.next(true);
      this.roleSubject.next(user.role || '');
    } else {
      this.currentUserSubject.next(null);
      this.isAuthenticatedSubject.next(false);
      this.roleSubject.next('');
    }
  }

  private getStoredUser(): User | null {
    try {
      return this.encryptionService.getItem<User>(this.STORAGE_KEY);
    } catch (error) {
      this.loggingService.log('Error retrieving encrypted user data:', error);
      return null;
    }
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Get current role
   */
  get role(): string {
    return this.roleSubject.value;
  }

  isSuperAdmin(): boolean {
    if (this.hasConnectAsSession()) {
      return false;
    }
    const user = this.getCurrentUser() || this.getStoredUser();
    return user?.role === 'SuperAdmin';
  }

  /** Workspace admin privileges — manager has the same access as admin. */
  isAdmin(): boolean {
    const user = this.getStoredUser();
    return user?.role === 'admin' || user?.role === 'manager';
  }

  isActiveUser(): boolean {
    const user = this.getStoredUser();
    return user?.status === 'active';
  }
  isManager(): boolean {
    const user = this.getStoredUser();
    return user?.role === 'manager';
  }

  isAdminOrManager(): boolean {
    return this.isAdmin() || this.isManager();
  }

  isAnalyst(): boolean {
    const user = this.getStoredUser();
    return user?.role === 'analyst';
  }

  isViewer(): boolean {
    const user = this.getStoredUser();
    return user?.role === 'viewer';
  }

  /**
   * Bot rail “Advanced AI Settings”: SuperAdmin only, or real “Connect to”
   * (Firebase `adminLogin` claim). Do not use localStorage `adminID` alone —
   * it can remain after a normal admin/manager login and wrongly unlock the menu.
   */
  canAccessAdvancedAiSettings(isAdminLoggedAsUser = false): boolean {
    if (isAdminLoggedAsUser) {
      return true;
    }
    const role =
      this.getCurrentUser()?.role || this.getStoredUser()?.role || '';
    // Admin / manager direct login: never show Advanced AI Settings.
    if (role === 'admin' || role === 'manager') {
      return false;
    }
    return role === 'SuperAdmin';
  }

  /**
   * “Add AI Instruction”: SuperAdmin in profile (Firestore / session / stored user), **or**
   * admin impersonation (`adminLogin`). While impersonating, profile roles belong to the client, so
   * `adminLogin` alone must allow the control.
   */
  canShowAddAiInstruction(
    firestoreUserRole: string | null | undefined,
    isAdminLoggedAsUser: boolean,
  ): boolean {
    if (isAdminLoggedAsUser) {
      return true;
    }
    const sessionRole = this.getCurrentUser()?.role;
    const storedRole = this.getStoredUser()?.role;
    return (
      firestoreUserRole === 'SuperAdmin' ||
      sessionRole === 'SuperAdmin' ||
      storedRole === 'SuperAdmin'
    );
  }

  hasConnectAsSession(): boolean {
    return !!this.getConnectAsAdminId();
  }
  canAccessSuperAdminOnlyFeatures(isAdminLoggedAsUser = false): boolean {
    if (isAdminLoggedAsUser || this.hasConnectAsSession()) {
      return true;
    }
    return this.canShowAddAiInstruction(this.getCurrentUser()?.role, false);
  }

  getBotsCollectionForSession(isAdminLoggedAsUser = false): string {
    if (isAdminLoggedAsUser || this.hasConnectAsSession()) {
      return 'bots';
    }
    return this.isSuperAdmin() ? 'default-bots' : 'bots';
  }

  /** Prefer the bot path / route `:type` over the session role. */
  resolveDesignCollection(
    routeType?: string | null,
    isAdminLoggedAsUser = false,
  ): 'bots' | 'default-bots' {
    if (routeType === 'default-bots' || routeType === 'bots') {
      return routeType;
    }
    return this.getBotsCollectionForSession(isAdminLoggedAsUser) ===
      'default-bots'
      ? 'default-bots'
      : 'bots';
  }

  collectionFromBotPath(path?: string | null): 'bots' | 'default-bots' | null {
    const value = String(path || '');
    if (value.includes('default-bots')) {
      return 'default-bots';
    }
    if (value.startsWith('bots/') || value === 'bots' || value.includes('/bots/')) {
      return 'bots';
    }
    return null;
  }

  parseDesignCollectionFromUrl(
    url: string,
    isAdminLoggedAsUser = false,
  ): 'bots' | 'default-bots' {
    const raw = (url || '').split('#')[0];
    const qIndex = raw.indexOf('?');
    const pathname = qIndex >= 0 ? raw.slice(0, qIndex) : raw;
    const query = qIndex >= 0 ? raw.slice(qIndex + 1) : '';
    const pathMatch = pathname.match(/\/(default-bots|bots)(?:\/|$)/);
    if (pathMatch?.[1] === 'default-bots' || pathMatch?.[1] === 'bots') {
      return pathMatch[1];
    }
    const typeParam = query
      .split('&')
      .map((part) => part.split('='))
      .find(([key]) => decodeURIComponent(key || '') === 'type');
    const queryType = typeParam?.[1] ? decodeURIComponent(typeParam[1]) : '';
    return this.resolveDesignCollection(queryType, isAdminLoggedAsUser);
  }

  replaceDesignCollectionInUrl(
    url: string,
    collection: 'bots' | 'default-bots',
  ): string {
    if (!url) {
      return url;
    }
    const [pathAndQuery, hash] = url.split('#');
    const qIndex = pathAndQuery.indexOf('?');
    const pathname = qIndex >= 0 ? pathAndQuery.slice(0, qIndex) : pathAndQuery;
    const query = qIndex >= 0 ? pathAndQuery.slice(qIndex + 1) : '';
    const nextPath = pathname.replace(
      /\/(bots|default-bots)(?=\/|$)/,
      `/${collection}`,
    );
    const nextQuery = query.replace(
      /(^|&)type=(bots|default-bots)/,
      `$1type=${collection}`,
    );
    const next =
      nextQuery.length > 0 ? `${nextPath}?${nextQuery}` : nextPath;
    return hash ? `${next}#${hash}` : next;
  }

  /** Signed-in Firebase uid (the impersonated admin during Connect to). */
  getFirebaseUid(): string {
    try {
      return firebase.auth().currentUser?.uid || '';
    } catch {
      return '';
    }
  }

  getFirebaseEmail(): string {
    try {
      return firebase.auth().currentUser?.email || '';
    } catch {
      return '';
    }
  }

  /**
   * Account a SuperAdmin connected to. Cleared with the Connect-to session.
   * Never stores the SuperAdmin uid.
   */
  setConnectedAccountId(uid: string): void {
    if (!this.isBrowser || !uid || uid === 'null' || uid === 'undefined') {
      return;
    }
    const impersonatorId = this.getConnectAsAdminId();
    if (impersonatorId && uid === impersonatorId) {
      return;
    }
    window.localStorage.setItem(this.CONNECTED_UID_KEY, uid);
    try {
      window.sessionStorage.setItem(this.CONNECTED_UID_KEY, uid);
    } catch {
      // sessionStorage can throw in locked-down browsers
    }
  }

  getConnectedAccountId(): string | null {
    if (!this.isBrowser) {
      return null;
    }
    let stored: string | null = null;
    try {
      stored =
        window.localStorage.getItem(this.CONNECTED_UID_KEY) ||
        window.sessionStorage.getItem(this.CONNECTED_UID_KEY);
    } catch {
      stored = window.localStorage.getItem(this.CONNECTED_UID_KEY);
    }
    if (!stored || stored === 'null' || stored === 'undefined') {
      return null;
    }
    const impersonatorId = this.getConnectAsAdminId();
    if (impersonatorId && stored === impersonatorId) {
      return null;
    }
    return stored;
  }

  /**
   * Uid that owns workspace bots / stores.
   * Managers → parent admin. Connect-to → impersonated account, never SuperAdmin.
   */
  getAccountUserId(): string {
    const user = this.getCurrentUser() || this.getStoredUser();
    const impersonatorId = this.getConnectAsAdminId() || '';
    const firebaseUid = this.getFirebaseUid();
    const profileId = user?.id || '';
    const parentId = user?.userParentId || '';
    const storedConnected = this.getConnectedAccountId() || '';

    if (impersonatorId) {
      const connectedUid =
        (storedConnected && storedConnected !== impersonatorId ? storedConnected : '') ||
        (firebaseUid && firebaseUid !== impersonatorId ? firebaseUid : '') ||
        (profileId && profileId !== impersonatorId ? profileId : '');
      const role = (user?.role || '').toLowerCase();
      if (
        role === 'manager' &&
        profileId &&
        profileId === connectedUid &&
        parentId &&
        parentId !== impersonatorId
      ) {
        return parentId;
      }
      if (connectedUid) {
        return connectedUid;
      }
    }

    return parentId || profileId || firebaseUid || '';
  }

  /**
   * Managers write to bots owned by the parent admin. Elevate the Firebase Auth
   * session with mintAdminToken(parentUid) so Firestore owner checks pass.
   * No-op for admins / users without userParentId.
   */
  async elevateToAccountOwnerIfManager(): Promise<void> {
    const user = this.getCurrentUser() || this.getStoredUser();
    const parentId = user?.userParentId;
    if (!parentId || user?.role !== 'manager') {
      return;
    }

    const callable = firebase.functions().httpsCallable('mintAdminToken');
    const res: any = await callable({ uid: parentId });
    const token = res?.data?.token ?? res?.token;
    if (!token) {
      throw new Error('mintAdminToken returned no token for account owner');
    }
    await firebase.auth().signInWithCustomToken(token);
  }
}
